import { useUserSlice } from "@/models/useUserSlice";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { AuthUser, cipher } from "@/api-lib";
import { useRouter } from "next/router";
import { getLocalizedBasePath } from "./utils";
import * as serverClient from "@/models/serverClient";
import toast from "react-hot-toast";
import { useUISlice } from "./useUISlice";
import { useTranslation } from "react-i18next";

export const useAuth = () => {
  const router = useRouter();
  const { t } = useTranslation();

  const _isValidEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim());
  };

  const currentAuthUser = (givenToken?: string): AuthUser | undefined => {
    const userToken = givenToken || useUserSlice.getState().userToken;

    if (!userToken) return undefined;

    return parseJwt(userToken);
  };

  function parseJwt(token: string): AuthUser {
    var base64Url = token.split(".")[1];
    var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    var jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    return JSON.parse(jsonPayload);
  }

  const goToDashboard = async (): Promise<{ success: boolean }> => {
    if (!(await isFreeUser())) {
      router.replace(`${getLocalizedBasePath()}/dashboard`);

      return { success: true };
    }

    return { success: false };
  };

  const signOut = () => {
    router.replace(`${getLocalizedBasePath()}/`);
    useUserSlice.getState().setUserToken(undefined);

    setTimeout(() => {
      window.location.reload();
    }, 300);
  };

  const signIn = ({
    forceSignIn = false,
    onGoogleSignIn,
    callback,
  }: {
    forceSignIn?: boolean;
    onGoogleSignIn?: () => void;
    callback?: (authToken: string) => void;
  }) => {
    useUISlice.getState().setAlertPromptSignIn({
      title: t("alertPromptSignIn.title"),
      subtitle: t("alertPromptSignIn.subtitle"),
      placeholder: t("alertPromptSignIn.signInEmailPlaceholder"),
      hideDismissIcon: forceSignIn,
      hideCancel: forceSignIn,
      onGoogleSignIn: onGoogleSignIn
        ? () => {
            onGoogleSignIn();
          }
        : undefined,
      shouldPersistBackdropClick: true,
      button: {
        text: t("auth.signInButton"),
        onPress: async (input) => {
          const email = input.trim().toLowerCase();
          const validEmail = _isValidEmail(email);

          if (!validEmail) {
            return toast.error(t("auth.invalidEmail"));
          }

          useUISlice.getState().setAlertPromptSignIn({
            ...useUISlice.getState().alertPromptSignIn,
            onGoogleSignIn: undefined,
            defaultValue: email,
            loading: true,
          });

          const otpRequestResponse = await serverClient.authenticateAsync({
            type: "OnOneTimeCodeRequested",
            payload: {
              email,
              deviceLanguage: navigator.language,
              deviceTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
          });

          useUISlice.getState().setAlertPromptSignIn({
            ...useUISlice.getState().alertPromptSignIn,
            onGoogleSignIn: undefined,
            loading: false,
          });

          if (!otpRequestResponse || !otpRequestResponse.data) {
            return toast.error(t("auth.invalidEmail"));
          }

          useUISlice.getState().setAlertPromptSignIn({
            title: t("auth.oneTimeCodeTitle"),
            subtitle: t("auth.oneTimeCodeSubtitle", { email }),
            placeholder: t("auth.oneTimeCodePlaceholder"),
            hideCancel: true,
            hideDismissIcon: forceSignIn,
            shouldPersistBackdropClick: true,
            button: {
              text: t("auth.oneTimeCodeButton"),
              onPress: async (input) => {
                useUISlice.getState().setAlertPromptSignIn({
                  ...useUISlice.getState().alertPromptSignIn,
                  defaultValue: input,
                  loading: true,
                });

                const otpVerifyResponse = await serverClient.authenticateAsync({
                  type: "OnOneTimeCodeSubmit",
                  payload: {
                    email,
                    oneTimeCode: parseInt(input),
                    deviceId: otpRequestResponse.data.deviceId,
                  },
                });

                useUISlice.getState().setAlertPromptSignIn({
                  ...useUISlice.getState().alertPromptSignIn,
                  loading: false,
                });

                if (
                  otpVerifyResponse &&
                  otpVerifyResponse.error === "InvalidOneTimeCode"
                ) {
                  return toast.error(t("auth.invalidCode"));
                }

                if (otpVerifyResponse && otpVerifyResponse.data) {
                  const { accessToken } = otpVerifyResponse.data;

                  useUserSlice.getState().setUserToken(accessToken);

                  const user = currentAuthUser(accessToken);

                  datadogRum.setUser({ id: user.deviceId, email: user.email });

                  datadogLogs.logger.info(
                    "Funnel 0: authenticated with one time code",
                    { user }
                  );

                  callback && callback(accessToken);

                  useUISlice.getState().setAlertPromptSignIn(undefined);
                }
              },
            },
          });
        },
      },
    });
  };

  const signInGuest = async () => {
    const dId = await serverClient.getDeviceIdAsync();
    const authUser = currentAuthUser();

    if (authUser && authUser.isAuthenticated) return;

    const authResponse = await serverClient.authenticateAsync({
      type: "OnGuessSignIn",
      payload: {
        accessToken: cipher({ deviceId: dId }),
        deviceId: dId,
        deviceLanguage: navigator.language,
        deviceTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });

    if (authResponse && authResponse.data) {
      const { accessToken } = authResponse.data;
      useUserSlice.getState().setUserToken(accessToken);

      const guestUser = currentAuthUser(accessToken);

      datadogRum.setUser({ id: guestUser?.deviceId, email: guestUser?.email });
      localStorage.setItem("_token", accessToken);

      datadogLogs.logger.info("Funnel 0: authenticated as guest", {
        user: guestUser,
      });
    }
  };

  const isFreeUser = async () => {
    const authUser = currentAuthUser();

    if (!authUser || !authUser.isAuthenticated) return true;

    const webUserStatusResponse = await serverClient.getWebUserStatusAsync();

    if (!webUserStatusResponse || !webUserStatusResponse.data) {
      useUserSlice.getState().setIsFree(true);
      return true;
    }

    useUserSlice.getState().setIsFree(webUserStatusResponse.data.isFree);

    return webUserStatusResponse.data.isFree;
  };

  return {
    isFreeUser,
    signIn,
    signOut,
    signInGuest,
    goToDashboard,
    currentAuthUser,
  };
};
